import {
  ClickAwayListener,
  Grow,
  MenuList,
  Paper,
  Popper,
  Typography,
  MenuItem,
  PopperPlacementType,
  IconButton,
  Menu,
  Divider,
  SxProps,
} from "@mui/material";
import React, { ReactElement, RefObject, useState, useEffect } from "react";
import {
  CalendarClock,
  Check,
  ChevronRight,
  FileDown,
  FilterX,
  Mail,
  Pencil,
  Pin,
  PinOff,
  Printer,
  Send,
  ShieldOff,
  Trash2,
} from "lucide-react";
import useIsMobile from "components/hooks/useIsMobile";

export default ({
  open,
  anchorRef,
  handleClose,
  children,
  placement,
  title,
}: {
  open: boolean;
  anchorRef: RefObject<HTMLElement>;
  handleClose: (event: Event) => void;
  children: ReactElement | ReactElement[];
  placement?: PopperPlacementType;
  title?: string;
}) => {
  return (
    <Popper
      sx={{ zIndex: 1 }}
      open={open}
      anchorEl={anchorRef.current}
      placement={placement ? placement : "bottom-start"}
      modifiers={[
        {
          name: "offset",
          options: {
            offset: [0, 8],
          },
        },
      ]}
      role={undefined}
      transition
      disablePortal
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin:
              placement === "bottom" ? "center top" : "center bottom",
          }}
        >
          <Paper
            sx={{
              borderRadius: "12px",
              border: "1px solid #E2E3E4",
              background: "#FFF",
              boxShadow: "0px 2px 8px 0px rgba(151, 93, 51, 0.07)",
              overflowY: "auto",
              padding: "8px 4px",
              maxHeight: "350px",
              scrollbarWidth: "thin",
            }}
          >
            <ClickAwayListener onClickAway={handleClose}>
              <MenuList sx={{ width: "100%" }}>
                {title && (
                  <Typography
                    variant="bodySmall"
                    color="text.tertiary"
                    sx={{ padding: "4px 16px 4px", fontWeight: "600" }}
                  >
                    {title}
                  </Typography>
                )}
                {children}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};

const getOptionIcon = (iconName: string) => {
  switch (iconName) {
    case "pencil":
      return <Pencil size={20} color="#8E9598" />;
    case "trash":
      return <Trash2 size={20} color="#8E9598" />;
    case "pin":
      return <Pin size={20} color="#8E9598" />;
    case "pinOff":
      return <PinOff size={20} color="#8E9598" />;
    case "mail":
      return <Mail size={20} color="#8E9598" />;
    case "send":
      return <Send size={20} color="#8E9598" />;
    case "filterX":
      return <FilterX size={20} color="#8E9598" />;
    case "scheduled":
      return <CalendarClock size={20} color="#8E9598" />;
    case "download":
      return <FileDown size={20} color="#8E9598" />;
    case "printer":
      return <Printer size={20} color="#8E9598" />;
    case "shieldOff":
      return <ShieldOff size={20} color="#8E9598" />;
    default:
      return null;
  }
};

export const createStaticMenuLine = (title: string) => (
  <MenuItem sx={{ padding: "6px 16px", cursor: "text" }} key={title}>
    <Typography color="text.tertiary" variant="bodySmall">
      {title}
    </Typography>
  </MenuItem>
);

export const createMenuOption = (
  title: string,
  onClick: () => void,
  iconName?: string,
  smallPadding = false,
) => {
  return (
    <MenuItem
      sx={{
        display: "flex",
        gap: "12px",
        padding: smallPadding ? "4px 16px" : "8px 16px",
        "&:hover": { background: "#FAF9FA" },
      }}
      onClick={onClick}
      key={title}
    >
      {iconName && getOptionIcon(iconName)}
      <Typography color="#3D3D3D" variant="body">
        {title}
      </Typography>
    </MenuItem>
  );
};

export const SubmenuMenuOption = ({
  title,
  onClick,
  submenuOptions,
  currentValue,
  disabled,
  showCheckmark = false,
  sx,
}: {
  title: string;
  onClick: (v: string) => void;
  submenuOptions: { label: string; value: string }[];
  currentValue?: string;
  disabled?: boolean;
  showCheckmark?: boolean;
  sx?: SxProps;
}) => {
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);
  const handleSubmenuClose = () => {
    setAnchorRect(null);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleMouseLeave = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      handleSubmenuClose();
    }, 200);
  };

  const handleMouseEnter = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const isMobile = useIsMobile();

  return (
    <>
      <MenuItem
        onClick={(event) =>
          setAnchorRect(event.currentTarget.getBoundingClientRect())
        }
        sx={{
          padding: "12px 16px",
          display: "flex",
          gap: "12px",
          justifyContent: "space-between",
          background: Boolean(anchorRect) ? "#FAF9FA" : "none",
          "&:hover": { background: "#FAF9FA" },
          ...sx,
        }}
        onMouseEnter={(event) => {
          setAnchorRect(event.currentTarget.getBoundingClientRect());
          handleMouseEnter();
        }}
        onMouseLeave={handleMouseLeave}
        key={title}
        disabled={disabled}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/*{getOptionIcon(iconName)}*/}
          <Typography color="#3D3D3D" variant="body">
            {(currentValue &&
              submenuOptions.find((o) => o.value === currentValue)?.label) ||
              title}
          </Typography>
        </div>
        <IconButton size="small">
          <ChevronRight size={20} />
        </IconButton>
      </MenuItem>
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={
          anchorRect
            ? {
                top: isMobile ? anchorRect.bottom : anchorRect.top - 8,
                left: isMobile ? anchorRect.left : anchorRect.right + 4,
              }
            : undefined
        }
        open={Boolean(anchorRect)}
        onClose={handleSubmenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 48 * 5.5,
              border: "1px solid #E2E3E4",
            },
          },
          // Remove the backdrop bottom so it doesn't cover the menu so
          // it doesn't trigger mouse leave
          root: {
            sx: {
              position: "absolute",
              bottom: "auto",
              ".MuiBackdrop-root": {
                position: "absolute",
                bottom: "auto",
              },
            },
          },
        }}
      >
        {submenuOptions.map((option) => {
          if (option.value === "divider") {
            return <Divider key={option.value} sx={{ margin: "8px 0" }} />;
          }

          const isSelected = currentValue === option.value;

          return (
            <MenuItem
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              key={option.value}
              onClick={() => {
                onClick(option.value);
                handleSubmenuClose();
              }}
              sx={{
                "&:hover": { background: "#FAF9FA" },
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography color="#3D3D3D" variant="body">
                {option.label}
              </Typography>
              {showCheckmark && isSelected && (
                <Check size={16} color="#198282" />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
